// Frontend/src/lib/overlay.ts
//
// Escape must close the TOPMOST overlay, not every open overlay at once.
//
// With one window listener per overlay, opening a lightbox over a detail
// sheet and pressing Escape closed both — the image and the record you were
// looking at, in one keystroke.
//
// Each overlay pushes an id while open and only acts on Escape when it is
// last. `body.overflow` is restored by the last overlay to close, not the
// first, so nesting cannot leave the page unscrollable.

import React from "react";

const stack: number[] = [];
let nextId = 1;

export function useOverlay(open: boolean, onClose: () => void) {
  React.useEffect(() => {
    if (!open) return;

    const id = nextId++;
    stack.push(id);

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (stack[stack.length - 1] !== id) return;
      e.stopPropagation();
      onClose();
    };

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);

    return () => {
      const at = stack.indexOf(id);
      if (at !== -1) stack.splice(at, 1);
      window.removeEventListener("keydown", onKey);
      if (stack.length === 0) document.body.style.overflow = previous;
    };
  }, [open, onClose]);
}
